import React, { useMemo } from 'react';
import { View, Animated, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { TESTIMONIALS } from '../../data/landingData';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

type ThemeColors = {
  background: string;
  text: string;
  secondary: string;
  card: string;
  border: string;
  cardText: string;
  primary: string;
};

type Props = {
  scrollAnimation: Animated.Value;
  colors: ThemeColors;
  isMobile?: boolean;
};

const CARD_GAP = 16;

const TestimonialsSection: React.FC<Props> = ({
  scrollAnimation,
  colors,
  isMobile = false,
}) => {
  const { screenWidth } = useResponsiveLayout();

  const { cardWidth, scrollTranslateEnd, cardHeight } = useMemo(() => {
    const w = isMobile ? Math.min(320, Math.max(160, screenWidth - 40)) : 320;
    const end = -(w + CARD_GAP) * TESTIMONIALS.length;
    const h = isMobile ? 248 : 280;
    return { cardWidth: w, scrollTranslateEnd: end, cardHeight: h };
  }, [isMobile, screenWidth]);

  // Render star rating row
  const renderStars = (rating: number) => (
    <View style={styles.stars}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Ionicons
          key={star}
          name={star <= rating ? 'star' : 'star-outline'}
          size={13}
          color="#F59E0B"
        />
      ))}
    </View>
  );

  // Render each testimonial card — extracted to avoid duplicated JSX below
  const renderCard = (testimonial: (typeof TESTIMONIALS)[0], key: string) => (
    <View
      key={key}
      style={[
        styles.card,
        {
          width: cardWidth,
          minWidth: cardWidth,
          maxWidth: cardWidth,
          height: cardHeight,
          padding: isMobile ? 18 : 24,
          backgroundColor: colors.card,
          borderColor: colors.border,
          ...(Platform.OS === 'web'
            ? { boxShadow: `0px 4px 20px 0px ${colors.cardText}15` }
            : {
                shadowColor: colors.cardText,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.1,
                shadowRadius: 12,
                elevation: 6,
              }),
        },
      ]}
      {...(Platform.OS === 'web'
        ? {
            itemScope: true,
            itemType: 'https://schema.org/Review',
            itemProp: 'review',
          }
        : {})}
    >
      <View style={styles.cardHeader}>
        <View
          style={[styles.quoteIcon, { backgroundColor: colors.primary + '15' }]}
        >
          <Ionicons name="chatbubble" size={16} color={colors.primary} />
        </View>
        {renderStars(testimonial.rating)}
      </View>

      <View style={styles.cardContent}>
        <Text
          style={[styles.quote, { color: colors.text }]}
          numberOfLines={4}
          ellipsizeMode="tail"
          {...(Platform.OS === 'web' ? { itemProp: 'reviewBody' } : {})}
        >
          {testimonial.quote}
        </Text>
      </View>

      <View style={styles.cardFooter}>
        <View style={styles.author}>
          <View
            style={[styles.avatar, { backgroundColor: colors.primary + '20' }]}
          >
            <Text style={[styles.avatarInitial, { color: colors.primary }]}>
              {testimonial.name.charAt(0)}
            </Text>
          </View>
          <View style={styles.authorMeta}>
            <Text
              style={[styles.authorName, { color: colors.text }]}
              numberOfLines={1}
              ellipsizeMode="tail"
              {...(Platform.OS === 'web' ? { itemProp: 'author' } : {})}
            >
              {testimonial.name}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.section,
        isMobile && styles.sectionMobile,
        { backgroundColor: colors.background },
      ]}
      {...(Platform.OS === 'web'
        ? {
            itemScope: true,
            itemType: 'https://schema.org/SoftwareApplication',
          }
        : {})}
    >
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text
          style={[
            styles.title,
            isMobile && styles.titleMobile,
            { color: colors.text },
          ]}
          {...(Platform.OS === 'web' ? { itemProp: 'name' } : {})}
        >
          Real People. Real Results.
        </Text>
        <Text
          style={[
            styles.subtitle,
            isMobile && styles.subtitleMobile,
            { color: colors.secondary },
          ]}
          {...(Platform.OS === 'web' ? { itemProp: 'description' } : {})}
        >
          Thousands of people have discovered the colours that make them shine.
        </Text>
      </View>

      <View
        style={[
          styles.scrollContainer,
          isMobile && styles.scrollContainerMobile,
        ]}
      >
        <Animated.View
          style={[
            styles.scrollRow,
            {
              transform: [
                {
                  translateX: scrollAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, scrollTranslateEnd],
                  }),
                },
              ],
            },
          ]}
        >
          {/* Two sets rendered for a seamless loop */}
          {TESTIMONIALS.map((t, i) => renderCard(t, `a-${i}`))}
          {TESTIMONIALS.map((t, i) => renderCard(t, `b-${i}`))}
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  sectionMobile: {
    paddingHorizontal: 16,
    paddingVertical: 18,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    flexGrow: 0,
    minWidth: 0,
  },
  header: {
    alignItems: 'center',
    marginBottom: 16,
  },
  headerMobile: {
    marginBottom: 12,
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: -0.8,
  },
  titleMobile: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 18,
    textAlign: 'center',
    lineHeight: 28,
    maxWidth: 500,
  },
  subtitleMobile: {
    fontSize: 14,
    lineHeight: 21,
    maxWidth: undefined,
  },
  scrollContainer: {
    flexDirection: 'row',
    overflow: 'hidden',
    marginBottom: 20,
  },
  scrollContainerMobile: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    flexGrow: 0,
    minWidth: 0,
  },
  scrollRow: {
    flexDirection: 'row',
    gap: CARD_GAP,
  },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  stars: {
    flexDirection: 'row',
    gap: 2,
  },
  quoteIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardContent: {
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: 0,
  },
  quote: {
    fontSize: 16,
    lineHeight: 24,
    fontStyle: 'italic',
    fontWeight: '400',
  },
  cardFooter: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
    paddingTop: 16,
  },
  author: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
  },
  authorMeta: {
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarInitial: {
    fontSize: 16,
    fontWeight: '600',
  },
  authorName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
});

export default TestimonialsSection;
